<script
  setup
  lang="ts"
  generic="
    T extends Record<string, string | number | undefined | null>,
    U extends keyof T
  "
>
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger
} from 'reka-ui'
import type { HTMLAttributes } from 'vue'

const props = defineProps<{
  class?: HTMLAttributes['class']
  itemClass?: HTMLAttributes['class']
  items: T[]
  valueKey?: U
  titleKey?: U
  contentKey?: U
}>()
</script>

<template>
  <AccordionRoot :class="cn('flex flex-col', props.class)">
    <AccordionItem
      v-for="(item, index) in items"
      :key="item[valueKey ?? 'value'] ?? index"
      :value="item[valueKey ?? 'value']?.toString() ?? index.toString()"
      :class="
        cn(
          'border border-outline-variant hover:bg-surface-container-low rounded-2xl',
          props.itemClass
        )
      "
    >
      <AccordionHeader class="flex flex-col">
        <AccordionTrigger class="group">
          <slot name="trigger" :item="item">
            <div class="flex items-center justify-between p-4 gap-4">
              <h3
                class="lg:text-lg font-semibold text-left"
                v-text="item[titleKey ?? 'title'] ?? undefined"
              />
              <Icon
                class="ease-[cubic-bezier(0.87,0,0.13,1)] transition-transform duration-300 group-data-[state=open]:rotate-180"
                name="material-symbols:keyboard-arrow-down"
                size="24px"
              />
            </div>
          </slot>
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent class="AccordionContent">
        <slot :item="item">
          <div class="px-4 prose max-w-none">
            <div
              class="border-t border-outline-variant py-4 has-[>p]:py-0"
              v-html="item[contentKey ?? 'content'] ?? undefined"
            />
          </div>
        </slot>
      </AccordionContent>
    </AccordionItem>
  </AccordionRoot>
</template>

<style scoped>
.AccordionContent {
  overflow: hidden;
}
.AccordionContent[data-state='open'] {
  animation: slideDown 300ms ease-out;
}
.AccordionContent[data-state='closed'] {
  animation: slideUp 300ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--reka-accordion-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-accordion-content-height);
  }
  to {
    height: 0;
  }
}
</style>
